import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { captureAPI } from '../services/api';
import { formatDocType } from '../utils/formatDocType';
import { CATEGORY_ORDER, CATEGORY_LABELS } from '../constants/fieldCategories';
import {
  DEFAULT_FIELD_SCOPE,
  resolveFieldScope,
  shouldShowScopeSelector,
} from '../utils/fieldScope';
import { VALUE_TYPES } from '../constants/fieldValueTypes';
import './FieldLibrarySettingsPage.css';


// Shown as the format input's tooltip. The DSL is the whole vocabulary a
// customer needs; the compiled regex is never surfaced (Ruling 1).
const FORMAT_LEGEND = [
  'Value format — # digit · A letter · ? any · [ ] optional',
  'Examples:  PO-#####   ACCT-####-AA',
  'Applies to this field across every vendor; vendor-specific shapes are learned by the vendor profile.',
  'Leave empty for no format.',
].join('\n');

const DEPT_COLORS = {
  accounts_payable:    '#2C6FB1',
  accounts_receivable: '#10b981',
  procurement:         '#f59e0b',
  contracts:           '#8b5cf6',
  human_resources:     '#ec4899',
  operations:          '#6b7280',
  finance:             '#14b8a6',
  general:             '#9ca3af',
};

const DEPT_LABELS = {
  accounts_payable:    'AP',
  accounts_receivable: 'AR',
  procurement:         'Procurement',
  contracts:           'Contracts',
  human_resources:     'HR',
  operations:          'Operations',
  finance:             'Finance',
  general:             'General',
};

// Doc-type labels are sourced from the iim_document_types registry at runtime (via
// captureAPI.getDocumentTypes → GET /api/capture/templates/document-types) — the single
// source of truth. No hardcoded label map (it drifted and omitted custom/tenant types).
// Fallback is formatDocType() (the shared humanizer), so labels never blank on fetch failure.

function DeptBadge({ deptKey }) {
  if (!deptKey) return null;
  const color = DEPT_COLORS[deptKey] || '#9ca3af';
  const label = DEPT_LABELS[deptKey] || deptKey;
  return (
    <span
      className="fls-dept-badge"
      style={{ background: color + '20', color, borderColor: color + '40' }}
    >
      {label}
    </span>
  );
}

function DocTypeBadge({ docType, label }) {
  return <span className="fls-doctype-badge">{label || formatDocType(docType)}</span>;
}

// Renders children in a document.body portal positioned relative to triggerRef.
// Flips upward when there isn't room below the trigger.
function PortalDropdown({ isOpen, triggerRef, className, children, gap = 4 }) {
  const [position, setPosition] = useState({ top: 0, left: 0, openUpward: false });

  const computePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const estimatedHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < estimatedHeight + gap && spaceAbove > spaceBelow;
    setPosition({
      top: openUpward
        ? rect.top + window.scrollY - gap
        : rect.bottom + window.scrollY + gap,
      left: rect.left + window.scrollX,
      openUpward,
    });
  }, [triggerRef, gap]);

  useEffect(() => {
    if (!isOpen) return;
    computePosition();
    const handler = () => computePosition();
    window.addEventListener('scroll', handler, { passive: true, capture: true });
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, { capture: true });
      window.removeEventListener('resize', handler);
    };
  }, [isOpen, computePosition]);

  if (!isOpen) return null;
  return createPortal(
    <div
      className={className}
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        ...(position.openUpward ? { transform: 'translateY(-100%)' } : {}),
      }}
    >
      {children}
    </div>,
    document.body
  );
}

function DocTypeDropdownCell({ field, isOpen, onToggle, onSelectType, docTypeOptions, resolveLabel }) {
  const triggerRef = useRef(null);
  const docTypes = (field.doc_types || []).filter(Boolean);
  return (
    <td>
      <div ref={triggerRef} className="fls-dropdown-container">
        <div
          className="fls-doctype-badges fls-editable"
          onClick={onToggle}
          title="Click to edit document types"
        >
          {docTypes.map(dt => (
            <DocTypeBadge key={dt} docType={dt} label={resolveLabel(dt)} />
          ))}
          {docTypes.length === 0 && (
            <span className="fls-none">+ Add</span>
          )}
        </div>
      </div>
      <PortalDropdown
        isOpen={isOpen}
        triggerRef={triggerRef}
        className="fls-dropdown fls-doctype-dropdown"
      >
        <div className="fls-dropdown-header">Document Types</div>
        {docTypeOptions.map(({ key, label }) => (
          <label key={key} className="fls-dropdown-item">
            <input
              type="checkbox"
              checked={(field.doc_types || []).includes(key)}
              onChange={() => onSelectType(key)}
            />
            <span>{label}</span>
          </label>
        ))}
      </PortalDropdown>
    </td>
  );
}

function OwnerDropdownCell({ field, isOpen, onToggle, onSelectOwner }) {
  const triggerRef = useRef(null);
  return (
    <td>
      <div ref={triggerRef} className="fls-dropdown-container">
        <div
          className="fls-editable"
          onClick={onToggle}
          title="Click to change owner department"
        >
          {field.owner_dept_key
            ? <DeptBadge deptKey={field.owner_dept_key} />
            : <span className="fls-none">+ Set</span>
          }
        </div>
      </div>
      <PortalDropdown
        isOpen={isOpen}
        triggerRef={triggerRef}
        className="fls-dropdown fls-owner-dropdown"
      >
        <div className="fls-dropdown-header">Owner Department</div>
        {Object.entries(DEPT_LABELS).map(([key, label]) => (
          <button
            key={key}
            className={`fls-dropdown-dept-item ${field.owner_dept_key === key ? 'active' : ''}`}
            onClick={() => onSelectOwner(key)}
          >
            <span
              className="fls-dept-dot"
              style={{ background: DEPT_COLORS[key] || '#9ca3af' }}
            />
            {label}
          </button>
        ))}
        {field.owner_dept_key && (
          <button
            className="fls-dropdown-dept-item fls-clear-owner"
            onClick={() => onSelectOwner(null)}
          >
            Clear owner
          </button>
        )}
      </PortalDropdown>
    </td>
  );
}

// Deduplicate fields: group all associations for the same field_key into one row
// The "primary" association is the first one found (lowest sort_order)
// Active and Extract toggles operate on ALL associations for that field_key
function deduplicateFields(fields) {
  const map = new Map();
  for (const f of fields) {
    const key = f.field_key;
    if (!map.has(key)) {
      map.set(key, { ...f, doc_types: [f.doc_type], _allIds: [f.id] });
    } else {
      const existing = map.get(key);
      if (f.doc_type && !existing.doc_types.includes(f.doc_type)) {
        existing.doc_types.push(f.doc_type);
      }
      existing._allIds.push(f.id);
    }
  }
  return Array.from(map.values());
}

export default function FieldLibrarySettingsPage() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);
  const [search, setSearch] = useState('');
  const [filterDocType, setFilterDocType] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newField, setNewField] = useState({ label: '', value_type: 'text', category: 'custom', scope: DEFAULT_FIELD_SCOPE, docTypes: [] });
  const [addError, setAddError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [openDocTypeDropdown, setOpenDocTypeDropdown] = useState(null);
  const [openOwnerDropdown, setOpenOwnerDropdown] = useState(null);

  // Does this tenant have the Document Library product? Drives whether the scope
  // selector is shown at all (see utils/fieldScope.js).
  //
  // Supplied by GET /api/capture/templates/field-library, which reads it from
  // the flags requireTenantCaptureProduct attaches to the request from the
  // tenant row it already fetches for the capture gate. Stays `null` until that
  // response lands, and `null` means UNKNOWN — which shows the selector, the
  // safe direction. Only a literal `false` hides it.
  const [hasLibrary, setHasLibrary] = useState(null);
  const showScopeSelector = shouldShowScopeSelector(hasLibrary);

  const loadFields = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await captureAPI.getFieldLibrary('capture');
      setFields(resp?.fields || []);
      // `?? null` and not `|| false`: a missing flag is unknown, not "no Library".
      setHasLibrary(resp?.hasLibrary ?? null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load field library');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadFields(); }, [loadFields]);

  // Close dropdowns when clicking outside both the trigger container and the portal'd panel
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !e.target.closest('.fls-dropdown-container') &&
        !e.target.closest('.fls-dropdown')
      ) {
        setOpenDocTypeDropdown(null);
        setOpenOwnerDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Doc-type registry (iim_document_types) — single source of truth for labels + assignable set.
  const [docTypeLabels, setDocTypeLabels] = useState({});          // key -> label
  const [registryDocTypes, setRegistryDocTypes] = useState([]);    // [{key,label}] active, sort_order

  const loadDocTypes = useCallback(async () => {
    try {
      const data = await captureAPI.getDocumentTypes();
      const list = Array.isArray(data?.documentTypes) ? data.documentTypes : [];
      const labels = {};
      const opts = [];
      for (const t of list) {
        if (!t?.key) continue;
        labels[t.key] = t.label || t.key;
        if (t.is_active !== false) opts.push({ key: t.key, label: t.label || t.key });
      }
      setDocTypeLabels(labels);
      setRegistryDocTypes(opts);
    } catch {
      // Defensive: leave empty → labels fall back to formatDocType(key); assignable list falls
      // back to the doc types present in the loaded fields. Page never blanks or breaks.
      setDocTypeLabels({});
      setRegistryDocTypes([]);
    }
  }, []);

  useEffect(() => { loadDocTypes(); }, [loadDocTypes]);

  // Registry-first label resolver; formatDocType() (the shared humanizer) is the last resort.
  const docTypeLabel = useCallback((key) => docTypeLabels[key] || formatDocType(key), [docTypeLabels]);

  // Deduplicate and group
  const deduplicated = useMemo(() => deduplicateFields(fields), [fields]);

  // Assignable doc types for the two association editors (per-field dropdown + add-form grid).
  // Registry-driven; if the registry is unavailable, fall back to the doc types present in fields.
  const assignableDocTypes = useMemo(() => {
    if (registryDocTypes.length > 0) return registryDocTypes;
    const present = [...new Set(deduplicated.flatMap((f) => f.doc_types || []).filter(Boolean))].sort();
    return present.map((k) => ({ key: k, label: formatDocType(k) }));
  }, [registryDocTypes, deduplicated]);

  // Derive filter options from loaded data
  const docTypeOptions = useMemo(() => {
    const types = [...new Set(deduplicated.flatMap(f => f.doc_types || []).filter(Boolean))];
    return types.sort();
  }, [deduplicated]);

  const ownerOptions = useMemo(() => {
    const owners = [...new Set(deduplicated.map(f => {
      const deptKey = f.owner_dept_key;
      return deptKey ? (DEPT_LABELS[deptKey] || deptKey) : null;
    }).filter(Boolean))];
    return owners.sort();
  }, [deduplicated]);

  const filtered = useMemo(() => {
    return deduplicated.filter(f => {
      // Search filter
      if (search) {
        const q = search.toLowerCase();
        const searchMatch = f.label?.toLowerCase().includes(q) ||
          f.field_key?.toLowerCase().includes(q) ||
          f.category?.toLowerCase().includes(q);
        if (!searchMatch) return false;
      }
      
      // Doc type filter
      if (filterDocType) {
        const docMatch = (f.doc_types || []).includes(filterDocType);
        if (!docMatch) return false;
      }
      
      // Owner filter
      if (filterOwner) {
        const ownerBadgeText = f.owner_dept_key ? (DEPT_LABELS[f.owner_dept_key] || f.owner_dept_key) : null;
        const ownerMatch = ownerBadgeText === filterOwner;
        if (!ownerMatch) return false;
      }
      
      return true;
    });
  }, [deduplicated, search, filterDocType, filterOwner]);

  const grouped = useMemo(() => {
    const groups = {};
    for (const f of filtered) {
      const cat = f.category || 'custom';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(f);
    }
    return groups;
  }, [filtered]);

  const orderedCategories = useMemo(() =>
    CATEGORY_ORDER.filter(c => grouped[c]?.length > 0)
      .concat(Object.keys(grouped).filter(c => !CATEGORY_ORDER.includes(c)))
  , [grouped]);

  // Toggle active — updates ALL associations for this field_key
  const handleToggleActive = async (field) => {
    setSaving(field.field_key);
    try {
      const newVal = !field.is_active;
      await Promise.all(field._allIds.map(id =>
        captureAPI.updateFieldLibraryEntry(id, { is_active: newVal })
      ));
      setFields(prev => prev.map(f =>
        f.field_key === field.field_key ? { ...f, is_active: newVal } : f
      ));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update field');
    } finally {
      setSaving(null);
    }
  };

  // Toggle extract (default_enabled) — updates ALL associations for this field_key
  const handleToggleExtract = async (field) => {
    if (!field.is_active) return;
    setSaving(field.field_key);
    try {
      const newVal = !field.default_enabled;
      await Promise.all(field._allIds.map(id =>
        captureAPI.updateFieldLibraryEntry(id, { default_enabled: newVal })
      ));
      setFields(prev => prev.map(f =>
        f.field_key === field.field_key ? { ...f, default_enabled: newVal } : f
      ));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update field');
    } finally {
      setSaving(null);
    }
  };

  // Toggle "sync to Library" (iim_fields.sync_to_library) — field-level; updates ALL
  // associations' underlying field for this field_key. Gated on is_active like Extract.
  const handleToggleSync = async (field) => {
    if (!field.is_active) return;
    setSaving(field.field_key);
    try {
      const newVal = !(field.sync_to_library !== false);
      await Promise.all(field._allIds.map(id =>
        captureAPI.updateFieldLibraryEntry(id, { sync_to_library: newVal })
      ));
      setFields(prev => prev.map(f =>
        f.field_key === field.field_key ? { ...f, sync_to_library: newVal } : f
      ));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update field');
    } finally {
      setSaving(null);
    }
  };

  const handleUpdateLabel = async (field, newLabel) => {
    if (!newLabel.trim() || newLabel === field.label) return;
    setSaving(field.field_key);
    try {
      // Update label via the first association id — backend updates iim_fields
      await captureAPI.updateFieldLibraryEntry(field._allIds[0], { label: newLabel.trim() });
      setFields(prev => prev.map(f =>
        f.field_key === field.field_key ? { ...f, label: newLabel.trim() } : f
      ));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update label');
    } finally {
      setSaving(null);
    }
  };

  // Declared value format (B4). Same onBlur shape as handleUpdateLabel, with two
  // differences the format needs and the label does not:
  //   - an emptied input is meaningful (it CLEARS the declaration), so the
  //     "nothing to do" test compares against the prior value rather than
  //     rejecting a blank;
  //   - a 400 is expected traffic here (a malformed DSL), so the input is reset
  //     to the prior value instead of being left showing something the server
  //     refused to store.
  const handleUpdateFormat = async (field, rawValue, inputEl) => {
    const prior = field.value_format ?? '';
    const trimmed = (rawValue ?? '').trim();
    if (trimmed === prior) return;
    setSaving(field.field_key);
    try {
      await captureAPI.updateFieldLibraryEntry(field._allIds[0], { value_format: trimmed || null });
      setFields(prev => prev.map(f =>
        f.field_key === field.field_key
          ? { ...f, value_format: trimmed || null, value_format_source: trimmed ? 'user' : 'default' }
          : f
      ));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update format');
      if (inputEl) inputEl.value = prior;
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (field) => {
    if (field.is_system || !field.is_tenant_owned) return;
    // Fork (tenant row + a shared global exists) → the field-level endpoint REVERTS to the
    // shared version rather than deleting. Pure custom (no global) → hard delete.
    const isFork = field.has_global;
    const confirmMsg = isFork
      ? `Reset "${field.label}" to the shared version? Your customizations to this field will be discarded.`
      : `Delete custom field "${field.label}"? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    setSaving(field.field_key);
    try {
      // Same field-level endpoint for both; the backend branches (fork → revert, custom → delete).
      // Reload to reflect the true post-state (row gone vs reverted to the shared global).
      await captureAPI.deleteCustomField(field.field_key);
      await loadFields();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error;
      setError(msg || (status === 409
        ? "Can't delete — field is in use."
        : (isFork ? 'Failed to reset field' : 'Failed to delete field')));
    } finally {
      setSaving(null);
    }
  };

  const handleToggleDocType = async (field, docTypeKey) => {
    const isCurrentlyAssociated = (field.doc_types || []).includes(docTypeKey);
    try {
      if (isCurrentlyAssociated) {
        // Remove association
        await captureAPI.removeFieldDocTypeAssociation(field.field_key, docTypeKey);
        setFields(prev => prev.map(f => {
          if (f.field_key !== field.field_key) return f;
          return { ...f, doc_types: (f.doc_types || []).filter(dt => dt !== docTypeKey) };
        }));
      } else {
        // Add association
        await captureAPI.addFieldDocTypeAssociation(field.field_key, docTypeKey, field.owner_dept_key);
        setFields(prev => prev.map(f => {
          if (f.field_key !== field.field_key) return f;
          return { ...f, doc_types: [...(f.doc_types || []), docTypeKey] };
        }));
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update document type association');
    }
  };

  const handleChangeOwner = async (field, newOwnerKey) => {
    try {
      await captureAPI.updateFieldOwner(field.field_key, newOwnerKey);
      setFields(prev => prev.map(f =>
        f.field_key === field.field_key ? { ...f, owner_dept_key: newOwnerKey } : f
      ));
      setOpenOwnerDropdown(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update owner department');
    }
  };

  const toggleNewDocType = (key) => {
    setNewField(prev => ({
      ...prev,
      docTypes: prev.docTypes.includes(key)
        ? prev.docTypes.filter(k => k !== key)
        : [...prev.docTypes, key],
    }));
  };

  const handleAddField = async (e) => {
    e.preventDefault();
    if (!newField.label.trim()) { setAddError('Label is required'); return; }
    if (newField.docTypes.length === 0) { setAddError('Select at least one document type'); return; }
    setAdding(true);
    setAddError(null);
    try {
      const [firstDocType, ...restDocTypes] = newField.docTypes;
      // Create the field + its first association (doc_type is required).
      // applies_to defaults to 'both' because definitions are shared, and is forced
      // to 'both' for a tenant with no Library — see utils/fieldScope.js.
      const resp = await captureAPI.createFieldLibraryEntry({
        label: newField.label,
        value_type: newField.value_type,
        category: newField.category,
        default_enabled: false,
        applies_to: resolveFieldScope({ hasLibrary, scope: newField.scope }),
        doc_type: firstDocType,
      });
      const fieldKey = resp?.field?.field_key;
      // Add the remaining doc-type associations.
      for (const dt of restDocTypes) {
        if (!fieldKey) break;
        await captureAPI.addFieldDocTypeAssociation(fieldKey, dt, null);
      }
      await loadFields();
      setNewField({ label: '', value_type: 'text', category: 'custom', scope: DEFAULT_FIELD_SCOPE, docTypes: [] });
      setShowAddForm(false);
    } catch (err) {
      setAddError(err?.response?.data?.error || 'Failed to create field');
    } finally {
      setAdding(false);
    }
  };

  const uniqueFieldCount = deduplicated.length;
  const activeCount = deduplicated.filter(f => f.is_active).length;
  const extractCount = deduplicated.filter(f => f.default_enabled).length;

  if (loading) {
    return <div className="fls-page"><p className="fls-loading">Loading field library…</p></div>;
  }

  return (
    <div className="fls-page">
      <div className="fls-header">
        <div>
          <h2>Field Library</h2>
          <p className="fls-subtitle">
            Fields are shared across document types. Toggle <strong>Active</strong> to show/hide a field, <strong>Extract</strong> to include it in AI extraction.
          </p>
        </div>
      </div>

      {error && (
        <div className="fls-error">
          {error}
          <button className="fls-error-close" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="fls-toolbar">
        <input
          type="text"
          className="fls-search"
          placeholder="Search fields…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="fls-toolbar-divider" />
        <select
          className={`fls-filter-select${filterDocType ? ' fls-filter-active' : ''}`}
          value={filterDocType}
          onChange={e => setFilterDocType(e.target.value)}
        >
          <option value="">All Doc Types</option>
          {docTypeOptions.map(dt => (
            <option key={dt} value={dt}>{docTypeLabel(dt)}</option>
          ))}
        </select>
        <select
          className={`fls-filter-select${filterOwner ? ' fls-filter-active' : ''}`}
          value={filterOwner}
          onChange={e => setFilterOwner(e.target.value)}
        >
          <option value="">All Owners</option>
          {ownerOptions.map(o => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
        {(filterDocType || filterOwner) && (
          <button
            className="fls-filter-clear"
            onClick={() => { setFilterDocType(''); setFilterOwner(''); }}
          >
            ✕ Clear Filters
          </button>
        )}
        <button className="fls-add-btn" onClick={() => setShowAddForm(v => !v)}>
          {showAddForm ? 'Cancel' : '+ Add Custom Field'}
        </button>
      </div>

      {showAddForm && (
        <form className="fls-add-form" onSubmit={handleAddField}>
          <div className="fls-add-row">
            <div className="fls-add-field">
              <label>Label</label>
              <input
                type="text"
                placeholder="e.g. Account Number"
                value={newField.label}
                onChange={e => setNewField(prev => ({ ...prev, label: e.target.value }))}
                required
                autoFocus
              />
            </div>
            <div className="fls-add-field">
              <label>Type</label>
              <select
                value={newField.value_type}
                onChange={e => setNewField(prev => ({ ...prev, value_type: e.target.value }))}
              >
                {VALUE_TYPES.map(vt => <option key={vt} value={vt}>{vt}</option>)}
              </select>
            </div>
            {/* Hidden for a tenant with no Document Library: the selector asks "does
                this field belong in the Library?", which is meaningless to a customer
                who has no Library. The submitted value is 'both' either way, so if they
                add Library later their existing fields are already there — writing
                'capture' would hand them an empty field list on upgrade, which looks
                broken. */}
            {showScopeSelector && (
              <div className="fls-add-field">
                <label>Scope</label>
                <select
                  value={newField.scope}
                  onChange={e => setNewField(prev => ({ ...prev, scope: e.target.value }))}
                >
                  <option value="both">Both products (shared)</option>
                  <option value="capture">Capture only</option>
                </select>
              </div>
            )}
            <div className="fls-add-field">
              <label>Category</label>
              <select
                value={newField.category}
                onChange={e => setNewField(prev => ({ ...prev, category: e.target.value }))}
              >
                {CATEGORY_ORDER.map(c => (
                  <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="fls-add-doctypes">
            <label className="fls-add-doctypes-label">Document types (select at least one)</label>
            <div className="fls-add-doctype-grid">
              {assignableDocTypes.map(({ key, label }) => (
                <label key={key} className="fls-add-doctype">
                  <input
                    type="checkbox"
                    checked={newField.docTypes.includes(key)}
                    onChange={() => toggleNewDocType(key)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="fls-add-actions">
            <button
              type="submit"
              className="fls-create-btn"
              disabled={adding || !newField.label.trim() || newField.docTypes.length === 0}
            >
              {adding ? 'Creating…' : 'Create Field'}
            </button>
          </div>
          {addError && <p className="fls-add-error">{addError}</p>}
        </form>
      )}

      <div className="fls-categories">
        {orderedCategories.map(cat => {
          const catFields = grouped[cat] || [];
          if (catFields.length === 0) return null;
          return (
            <div key={cat} className="fls-category">
              <h3 className="fls-category-title">
                {CATEGORY_LABELS[cat] || cat}
                <span className="fls-category-count">{catFields.length}</span>
              </h3>
              <table className="fls-table">
                <thead>
                  <tr>
                    <th className="fls-th-check">Active</th>
                    <th className="fls-th-check">Extract</th>
                    <th className="fls-th-check">Sync</th>
                    <th>Label</th>
                    <th>Key</th>
                    <th>Type</th>
                    <th>Format</th>
                    <th>Doc Types</th>
                    <th>Owner</th>
                    <th className="fls-th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {catFields.map(f => (
                    <tr
                      key={f.field_key}
                      className={[
                        'fls-row',
                        !f.is_active ? 'fls-inactive' : '',
                        saving === f.field_key ? 'fls-saving' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <td className="fls-td-check">
                        <input
                          type="checkbox"
                          checked={f.is_active}
                          onChange={() => handleToggleActive(f)}
                          disabled={saving === f.field_key}
                        />
                      </td>
                      <td className="fls-td-check">
                        <input
                          type="checkbox"
                          checked={f.default_enabled || false}
                          disabled={!f.is_active || saving === f.field_key}
                          onChange={() => handleToggleExtract(f)}
                          title={f.is_active ? 'Toggle AI extraction' : 'Activate field first'}
                        />
                      </td>
                      <td className="fls-td-check">
                        <input
                          type="checkbox"
                          checked={f.sync_to_library !== false}
                          disabled={!f.is_active || saving === f.field_key}
                          onChange={() => handleToggleSync(f)}
                          title={f.is_active ? 'Sync this field to the Document Library on publish' : 'Activate field first'}
                        />
                      </td>
                      <td>
                        <input
                          className="fls-label-input"
                          defaultValue={f.label}
                          onBlur={e => handleUpdateLabel(f, e.target.value)}
                          disabled={saving === f.field_key}
                        />
                      </td>
                      <td>
                        <code className="fls-key">{f.field_key}</code>
                        {f.is_system && <span className="fls-system-badge">system</span>}
                      </td>
                      <td className="fls-td-type">{f.value_type}</td>
                      <td className="fls-td-format">
                        {f.value_format_editable === false ? (
                          // currency / number / date: the type engine owns
                          // validation and the DSL cannot express it, so the
                          // default is shown read-only and the PATCH refuses an
                          // edit. Nothing to click.
                          <code className="fls-format-managed" title="Managed by the field type">
                            {f.value_format_default || '—'}
                          </code>
                        ) : (
                          <>
                            <input
                              className="fls-format-input"
                              defaultValue={f.value_format ?? ''}
                              placeholder="none"
                              title={FORMAT_LEGEND}
                              onBlur={e => handleUpdateFormat(f, e.target.value, e.target)}
                              disabled={saving === f.field_key}
                            />
                            {f.value_format_source === 'user' && (
                              <span className="fls-custom-badge">custom</span>
                            )}
                          </>
                        )}
                      </td>
                      <DocTypeDropdownCell
                        field={f}
                        isOpen={openDocTypeDropdown === f.field_key}
                        onToggle={() => setOpenDocTypeDropdown(
                          openDocTypeDropdown === f.field_key ? null : f.field_key
                        )}
                        onSelectType={(docTypeKey) => handleToggleDocType(f, docTypeKey)}
                        docTypeOptions={assignableDocTypes}
                        resolveLabel={docTypeLabel}
                      />
                      <OwnerDropdownCell
                        field={f}
                        isOpen={openOwnerDropdown === f.field_key}
                        onToggle={() => setOpenOwnerDropdown(
                          openOwnerDropdown === f.field_key ? null : f.field_key
                        )}
                        onSelectOwner={(newOwnerKey) => handleChangeOwner(f, newOwnerKey)}
                      />
                      <td className="fls-td-actions">
                        {/* Three-way: pure custom → Delete; fork → Reset to shared; shared
                            global non-system (not tenant-owned) → no destructive control. */}
                        {!f.is_system && f.is_tenant_owned && (
                          <button
                            className={f.has_global ? 'fls-reset-btn' : 'fls-delete-btn'}
                            onClick={() => handleDelete(f)}
                            disabled={saving === f.field_key}
                            title={f.has_global ? 'Reset to the shared version' : 'Delete custom field'}
                          >
                            {f.has_global ? 'Reset to shared' : 'Delete'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      <div className="fls-footer">
        <span>{uniqueFieldCount} unique fields</span>
        <span>·</span>
        <span>{activeCount} active</span>
        <span>·</span>
        <span>{extractCount} extracted by default</span>
      </div>
    </div>
  );
}
